import { Box, Stack, Typography } from '@mui/material';

export const FALLBACK_DATE_YEARS = [1900, 1970];

export const isFallbackDateYear = (year) => FALLBACK_DATE_YEARS.includes(Number(year));

export const parseDateFromString = (value) => {
    if (!value || typeof value !== 'string') return null;

    const matches = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/g);
    if (!matches?.length) return null;

    const [d, m, y] = matches[0].split('/').map(Number);
    if (!d || !m || !y) return null;

    const date = new Date(y, m - 1, d);
    if (Number.isNaN(date.getTime()) || isFallbackDateYear(date.getFullYear())) return null;
    return date;
};

export const parseLookupDate = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) || isFallbackDateYear(value.getFullYear()) ? null : value;
    }

    if (typeof value !== 'string') return null;

    const trimmed = value.trim();
    if (!trimmed) return null;

    const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
        const date = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
        return Number.isNaN(date.getTime()) || isFallbackDateYear(date.getFullYear()) ? null : date;
    }

    return parseDateFromString(trimmed);
};

export const formatDateForQuery = (date) =>
    `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;

export const parseEndDateFromString = (value, fallbackStart) => {
    if (!value || typeof value !== 'string') return fallbackStart;

    const matches = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/g);
    if (!matches?.length) return fallbackStart;

    const raw = matches.length > 1 ? matches[matches.length - 1] : matches[0];
    const [d, m, y] = raw.split('/').map(Number);
    const date = new Date(y, m - 1, d);

    if (Number.isNaN(date.getTime()) || isFallbackDateYear(date.getFullYear())) return fallbackStart;
    return date;
};

export const parseCompetitionDateRange = (value) => {
    if (!value || typeof value !== 'string') {
        return { start: null, end: null, hasRange: false };
    }

    const trimmed = value.trim();
    const dayRangeWithMonthYear = trimmed.match(/^(\d{1,2})\s*-\s*(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dayRangeWithMonthYear) {
        const fromDay = Number(dayRangeWithMonthYear[1]);
        const toDay = Number(dayRangeWithMonthYear[2]);
        const month = Number(dayRangeWithMonthYear[3]);
        const year = Number(dayRangeWithMonthYear[4]);
        const start = new Date(year, month - 1, fromDay);
        const end = new Date(year, month - 1, toDay);

        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
            return { start, end, hasRange: true };
        }
    }

    const fullRange = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s*-\s*(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (fullRange) {
        const start = new Date(Number(fullRange[3]), Number(fullRange[2]) - 1, Number(fullRange[1]));
        const end = new Date(Number(fullRange[6]), Number(fullRange[5]) - 1, Number(fullRange[4]));

        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
            return { start, end, hasRange: true };
        }
    }

    const start = parseDateFromString(trimmed);
    if (!start) return { start: null, end: null, hasRange: false };

    return { start, end: start, hasRange: false };
};

export const isSameCalendarDay = (left, right) =>
    Boolean(
        left &&
            right &&
            left.getFullYear() === right.getFullYear() &&
            left.getMonth() === right.getMonth() &&
            left.getDate() === right.getDate()
    );

export const getCompetitionCalendarDateRange = (item) => {
    const originalStart = parseLookupDate(item?.StartDate ?? item?.startDate);
    const originalEnd = parseLookupDate(item?.EndDate ?? item?.endDate);

    if (originalStart) {
        const end = originalEnd || originalStart;
        return {
            start: originalStart,
            end,
            hasRange: !isSameCalendarDay(originalStart, end)
        };
    }

    const parsedRange = parseCompetitionDateRange(item?.Date ?? item?.date);
    if (!parsedRange.start) return parsedRange;

    return parsedRange;
};

export const getMonthLabel = (monthIndex, locale, format = 'long') => {
    const date = new Date(2026, monthIndex, 1);
    return new Intl.DateTimeFormat(locale || 'en', { month: format }).format(date);
};

export const CalendarDayTile = ({ date, locale }) => (
    <Box
        sx={{
            width: 56,
            height: 70,
            borderRadius: 1.4,
            overflow: 'hidden',
            backgroundColor: '#fff',
            border: '1px solid #d8e0ea',
            boxShadow: '0 4px 10px rgba(15, 23, 42, 0.08)',
            position: 'relative',
            flexShrink: 0
        }}
    >
        <Box sx={{ height: 15, backgroundColor: '#f44336', position: 'relative' }}>
            <Box
                sx={{
                    position: 'absolute',
                    top: -5,
                    left: 12,
                    width: 7,
                    height: 18,
                    borderRadius: 10,
                    backgroundColor: '#455a64',
                    border: '2px solid #fff'
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    top: -5,
                    right: 12,
                    width: 7,
                    height: 18,
                    borderRadius: 10,
                    backgroundColor: '#455a64',
                    border: '2px solid #fff'
                }}
            />
        </Box>
        <Box
            sx={{
                height: 55,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.25
            }}
        >
            <Typography sx={{ color: '#e53935', fontWeight: 800, fontSize: '0.78rem', lineHeight: 1 }}>
                {getMonthLabel(date.getMonth(), locale, 'short').replace('.', '')}
            </Typography>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: '#2f464a', lineHeight: 1 }}>
                {String(date.getDate()).padStart(2, '0')}
            </Typography>
        </Box>
    </Box>
);

export const CompetitionDateBlock = ({ item, locale, fallbackDate }) => {
    const { start, end, hasRange } = getCompetitionCalendarDateRange(item);

    if (!start) {
        return (
            <Box sx={{ minWidth: 95 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{fallbackDate || item?.Date || item?.date || '-'}</Typography>
            </Box>
        );
    }

    const startYear = start.getFullYear();
    const endYear = end.getFullYear();
    const yearLabel = hasRange && startYear !== endYear ? `${startYear} - ${endYear}` : String(startYear);

    return (
        <Box sx={{ minWidth: hasRange ? 138 : 56 }}>
            <Stack direction="row" spacing={0.8} alignItems="center">
                <CalendarDayTile date={start} locale={locale} />
                {hasRange && (
                    <>
                        <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#253858', lineHeight: 1 }}>-</Typography>
                        <CalendarDayTile date={end} locale={locale} />
                    </>
                )}
            </Stack>
            <Typography
                sx={{
                    mt: 0.55,
                    color: '#315b9f',
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    lineHeight: 1,
                    whiteSpace: 'nowrap'
                }}
            >
                {yearLabel}
            </Typography>
        </Box>
    );
};
